<template>
  <div class="asset-view-wrapper">
    <div class="assets-table-container">
      <div class="assets-grid-card">
        <div class="assets-grid-card__header">
          <div class="assets-grid-title">
            <div class="assets-grid-title__icon">
              <i class="bi bi-diagram-3-fill" />
            </div>
            <div>
              <div class="assets-grid-title__text">
                {{ isDraft ? $t('components.infrastructure.anchorPlacement.addScheme') : (scheme?.name || $t('components.assetSchemes.editor.title')) }}
              </div>
              <div class="text-muted small">
                {{ isDraft ? displayGroupName : (scheme?.group?.name || '-') }}
              </div>
            </div>
          </div>

          <div class="assets-grid-controls">
            <button
              type="button"
              class="assets-toolbar-btn"
              :title="$t('common.back')"
              @click="$router.push({ name: 'AssetSchemes' })"
            >
              <i class="bi bi-arrow-left" />
              <span>{{ $t('common.back') }}</span>
            </button>
          </div>
        </div>

        <div class="assets-grid-card__body">
          <div
            v-if="errorMessage"
            class="alert alert-danger d-flex align-items-center gap-2 mb-3"
          >
            <i class="bi bi-exclamation-triangle-fill" />
            <span>{{ errorMessage }}</span>
          </div>
          <div
            v-if="loading"
            class="wp-loading"
          >
            <span
              class="spinner-border wp-loading__spinner"
              role="status"
            />
            <span class="wp-loading__text">{{ $t('components.assetSchemes.loading') }}</span>
          </div>
          <div v-else>
            <div
              v-if="!canEdit"
              class="alert alert-info d-flex align-items-center gap-2 mb-3"
            >
              <i class="bi bi-info-circle-fill" />
              <span>{{ $t('components.assetSchemes.editor.readOnly') }}</span>
            </div>
            <SchemeEditor
              ref="editorRef"
              :scheme-image="schemeImage"
              :markers="schemeMarkers"
              :assets="assetsForGroup"
              :anchors="anchors"
              :initial-mode="initialMode"
              :read-only="!canEdit"
              :calibration="isDraft ? { widthM: calibrationForm.widthM, heightM: calibrationForm.heightM } : { widthM: scheme?.widthM ?? null, heightM: scheme?.heightM ?? null }"
            >
              <template
                v-if="canEdit"
                #side-top
              >
                <div
                  v-if="isDraft"
                  class="scheme-panel"
                >
                  <div class="scheme-panel__header">
                    <i class="bi bi-diagram-3-fill scheme-panel__icon" />
                    <span class="scheme-panel__title">{{ $t('components.infrastructure.anchorPlacement.addScheme') }}</span>
                  </div>
                  <div
                    v-if="errorMessage"
                    class="alert alert-danger py-1 px-2 mb-2 small"
                  >
                    {{ errorMessage }}
                  </div>
                  <div class="mb-2">
                    <label
                      class="form-label d-block mb-1"
                      :class="{ required: !presetGroupId }"
                    >{{ $t('components.infrastructure.anchorPlacement.form.location') }}</label>
                    <select
                      v-if="!presetGroupId"
                      v-model.number="draftGroupId"
                      class="form-select form-select-sm w-100"
                      :class="{ 'is-invalid': formTouched && !draftGroupId }"
                    >
                      <option
                        :value="null"
                        disabled
                      >
                        {{ $t('components.infrastructure.anchorPlacement.form.locationPlaceholder') }}
                      </option>
                      <option
                        v-for="loc in locationOptions"
                        :key="loc.id"
                        :value="loc.id"
                      >
                        {{ loc.name }}
                      </option>
                    </select>
                    <input
                      v-else
                      type="text"
                      class="form-control form-control-sm w-100"
                      :value="displayGroupName"
                      disabled
                    >
                    <div
                      v-if="formTouched && !presetGroupId && !draftGroupId"
                      class="invalid-feedback"
                    >
                      {{ $t('components.infrastructure.anchorPlacement.form.locationRequired') }}
                    </div>
                  </div>
                  <div class="mb-2">
                    <label class="form-label d-block mb-1 required">{{ $t('components.infrastructure.anchorPlacement.form.name') }}</label>
                    <input
                      v-model="draftName"
                      type="text"
                      class="form-control form-control-sm w-100"
                      :class="{ 'is-invalid': formTouched && !draftName.trim() }"
                      maxlength="255"
                      :placeholder="$t('components.infrastructure.anchorPlacement.form.namePlaceholder')"
                    >
                    <div
                      v-if="formTouched && !draftName.trim()"
                      class="invalid-feedback"
                    >
                      {{ $t('components.infrastructure.anchorPlacement.form.nameRequired') }}
                    </div>
                  </div>
                  <input
                    ref="draftFileInputRef"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    hidden
                    @change="onDraftFileChange"
                  >
                  <button
                    type="button"
                    class="tab-link active w-100 text-center mb-2"
                    @click="triggerDraftFilePicker"
                  >
                    <i class="bi bi-upload me-1" />
                    {{ draftFile ? draftFile.name : $t('components.infrastructure.anchorPlacement.form.file') }}
                  </button>
                  <div class="mb-2">
                    <label
                      class="form-label d-block mb-1"
                      :class="{ required: !draftFile }"
                    >{{ $t('components.assetSchemes.editor.calibration.widthM') }}</label>
                    <input
                      :value="calibrationForm.widthM"
                      type="number"
                      step="0.1"
                      min="0"
                      class="form-control form-control-sm w-100"
                      :class="{ 'is-invalid': formTouched && (!draftFile ? !(calibrationForm.widthM > 0) : !isCalibrationValid) }"
                      @input="onWidthInput($event)"
                    >
                    <div
                      v-if="formTouched && !draftFile && !(calibrationForm.widthM > 0)"
                      class="invalid-feedback"
                    >
                      {{ $t('components.infrastructure.anchorPlacement.form.widthRequired') }}
                    </div>
                  </div>
                  <div class="mb-2">
                    <label
                      class="form-label d-block mb-1"
                      :class="{ required: !draftFile }"
                    >{{ $t('components.assetSchemes.editor.calibration.heightM') }}</label>
                    <input
                      v-model.number="calibrationForm.heightM"
                      type="number"
                      step="0.1"
                      min="0"
                      class="form-control form-control-sm w-100"
                      :class="{ 'is-invalid': formTouched && (!draftFile ? !(calibrationForm.heightM > 0) : !isCalibrationValid) }"
                    >
                    <div
                      v-if="formTouched && !draftFile && !(calibrationForm.heightM > 0)"
                      class="invalid-feedback"
                    >
                      {{ $t('components.infrastructure.anchorPlacement.form.heightRequired') }}
                    </div>
                    <div
                      v-else-if="formTouched && draftFile && !isCalibrationValid"
                      class="invalid-feedback"
                    >
                      {{ $t('components.assetSchemes.editor.calibration.errors.bothRequired') }}
                    </div>
                  </div>
                  <p class="scheme-editor__panel-hint">
                    {{ draftFile ? $t('components.infrastructure.anchorPlacement.form.dimensionsHintWithFile') : $t('components.infrastructure.anchorPlacement.form.dimensionsHint') }}
                  </p>
                  <div
                    v-if="createError"
                    class="alert alert-danger py-1 px-2 mb-0 small"
                  >
                    {{ createError }}
                  </div>
                </div>
                <div
                  v-else
                  class="scheme-panel"
                >
                  <div class="scheme-panel__header">
                    <i class="bi bi-aspect-ratio scheme-panel__icon" />
                    <span class="scheme-panel__title">{{ $t('components.assetSchemes.editor.calibration.title') }}</span>
                  </div>
                  <div
                    v-if="calibrationError"
                    class="alert alert-danger py-1 px-2 mb-2 small"
                  >
                    {{ calibrationError }}
                  </div>
                  <div
                    v-if="scheme?.widthM == null && hasAnchorMarkers"
                    class="alert alert-warning py-1 px-2 mb-2 small"
                  >
                    {{ $t('components.assetSchemes.editor.calibration.existingAnchorsWarning') }}
                  </div>
                  <div
                    v-if="imageError"
                    class="alert alert-danger py-1 px-2 mb-2 small"
                  >
                    {{ imageError }}
                  </div>
                  <input
                    ref="imageFileInputRef"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    hidden
                    @change="onImageFileChange"
                  >
                  <div
                    v-if="scheme?.filePath"
                    class="row g-2 mb-2"
                  >
                    <div class="col-6">
                      <button
                        type="button"
                        class="tab-link tab-link--compact active w-100 text-center"
                        :disabled="imageSaving"
                        @click="triggerImagePicker"
                      >
                        <span
                          v-if="imageSaving"
                          class="spinner-border spinner-border-sm me-1"
                        />
                        {{ $t('components.assetSchemes.editor.calibration.changeImage') }}
                      </button>
                    </div>
                    <div class="col-6">
                      <button
                        type="button"
                        class="tab-link tab-link--compact tab-link--danger w-100 text-center"
                        :disabled="imageSaving"
                        @click="removeImage"
                      >
                        {{ $t('components.assetSchemes.editor.calibration.removeImage') }}
                      </button>
                    </div>
                  </div>
                  <button
                    v-else
                    type="button"
                    class="tab-link active w-100 text-center mb-2"
                    :disabled="imageSaving"
                    @click="triggerImagePicker"
                  >
                    <span
                      v-if="imageSaving"
                      class="spinner-border spinner-border-sm me-1"
                    />
                    <i
                      v-else
                      class="bi bi-upload me-1"
                    />
                    {{ $t('components.assetSchemes.editor.calibration.uploadImage') }}
                  </button>
                  <div class="mb-2">
                    <label class="form-label d-block mb-1">{{ $t('components.assetSchemes.editor.calibration.widthM') }}</label>
                    <input
                      :value="calibrationForm.widthM"
                      type="number"
                      step="0.1"
                      min="0"
                      class="form-control form-control-sm w-100"
                      :class="{ 'is-invalid': formTouched && !isCalibrationValid }"
                      @input="onWidthInput($event)"
                    >
                  </div>
                  <div class="mb-2">
                    <label class="form-label d-block mb-1">{{ $t('components.assetSchemes.editor.calibration.heightM') }}</label>
                    <input
                      v-model.number="calibrationForm.heightM"
                      type="number"
                      step="0.1"
                      min="0"
                      class="form-control form-control-sm w-100"
                      :class="{ 'is-invalid': formTouched && !isCalibrationValid }"
                    >
                    <div
                      v-if="formTouched && !isCalibrationValid"
                      class="invalid-feedback"
                    >
                      {{ $t('components.assetSchemes.editor.calibration.errors.bothRequired') }}
                    </div>
                  </div>
                  <p class="scheme-editor__panel-hint">
                    {{ $t('components.assetSchemes.editor.calibration.widthLocksHeight') }}
                  </p>
                  <button
                    v-if="scheme?.widthM != null"
                    type="button"
                    class="tab-link tab-link--compact tab-link--outline-danger w-100 mt-1 text-center"
                    :disabled="clearingCalibration"
                    @click="clearCalibration"
                  >
                    <span
                      v-if="clearingCalibration"
                      class="spinner-border spinner-border-sm me-1"
                    />
                    {{ $t('components.assetSchemes.editor.calibration.clear') }}
                  </button>
                </div>
              </template>
            </SchemeEditor>
          </div>
        </div>

        <div
          v-if="canEdit"
          class="assets-grid-card__body border-top"
        >
          <div class="d-flex justify-content-end">
            <button
              type="button"
              class="tab-link active"
              :disabled="saving || creatingScheme"
              @click="save"
            >
              <span
                v-if="saving || creatingScheme"
                class="spinner-border spinner-border-sm me-2"
              />
              {{ $t('common.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import SchemeEditor from '@/components/content/SchemeEditor.vue'
import { getAssetScheme } from '@/crud/asset-scheme'
import { updateAssetSchemeMarkers, updateAssetSchemeCalibration, replaceAssetSchemeImage, removeAssetSchemeImage } from '@/crud/asset-scheme'
import { getAssetsWithLatestParams } from '@/crud/asset/getters'
import { getAssetGroup, getAssetGroupsWithLocation, addAssetGroupScheme } from '@/crud/asset-group'
import { fetchAnchorsForLocation, fetchAnchorsForGroup } from '@/composables/useSchemeAnchors'
import { canEditAssetGroups } from '@/utils/tools'

export default defineComponent({
  name: 'AssetSchemeEditorView',
  components: { SchemeEditor },
  props: {
    id: { type: [String, Number], required: true }
  },
  data() {
    return {
      scheme: null as any,
      schemeImage: null as string | null,
      schemeMarkers: [] as any[],
    assetsCache: [] as any[],
    groupAssets: [] as any[],
    anchors: [] as any[],
    loading: false,
    saving: false,
    calibrationForm: { widthM: null, heightM: null } as { widthM: number | null; heightM: number | null },
    clearingCalibration: false,
    calibrationError: null as string | null,
    imageSaving: false,
    imageError: null as string | null,
    errorMessage: null as string | null,
    refreshInterval: null as any,
    draftGroupId: null as number | null,
    draftName: '',
    draftFile: null as File | null,
    locationOptions: [] as { id: number; name: string }[],
    creatingScheme: false,
    createError: null as string | null,
    // True once the user has tried to hit "Zapisz" at least once -- field-level errors only
    // show from that point on, same as the Nowy użytkownik assetów modal's v$.$touch() pattern.
    formTouched: false
    }
  },
  computed: {
    // "new" is a synthetic id: the location has no scheme yet, so this view shows a blank
    // rectangle + create form instead of fetching/editing an existing scheme.
    isDraft(): boolean {
      return this.id === 'new'
    },
    presetGroupId(): number | null {
      const q = this.$route.query.groupId
      return q ? Number(q) : null
    },
    groupId(): number | null {
      if (this.isDraft) return this.presetGroupId ?? this.draftGroupId
      return this.scheme?.group?.id ?? null
    },
    displayGroupName(): string {
      if (this.presetGroupId) return (this.$route.query.groupName as string) || '-'
      const found = this.locationOptions.find(loc => loc.id === this.draftGroupId)
      return found?.name || '-'
    },
    canCreateDraft(): boolean {
      if (!this.groupId || !this.draftName.trim()) return false
      // Dimensions are optional once a file is picked (the image can still carry real-world
      // calibration), but if either is filled in, both must be -- partial is never valid.
      if (!this.isCalibrationValid) return false
      if (this.draftFile) return true
      return this.calibrationForm.widthM !== null && this.calibrationForm.heightM !== null
    },
    // Backend requires widthM/heightM together or neither -- valid to leave both empty (no
    // calibration yet), invalid to fill only one.
    isCalibrationValid(): boolean {
      const widthSet = this.calibrationForm.widthM !== null && this.calibrationForm.widthM !== undefined
      const heightSet = this.calibrationForm.heightM !== null && this.calibrationForm.heightM !== undefined
      if (!widthSet && !heightSet) return true
      if (widthSet && heightSet) return (this.calibrationForm.widthM as number) > 0 && (this.calibrationForm.heightM as number) > 0
      return false
    },
    canEdit(): boolean {
      return canEditAssetGroups()
    },
    hasAnchorMarkers(): boolean {
      return this.schemeMarkers.some((m: any) => m.type === 'anchor')
    },
    // Lets other views (e.g. the Infrastructure "Kotwice" tab) deep-link straight into anchor
    // placement mode via /asset-schemes/:id/edit?mode=anchor.
    initialMode(): 'asset' | 'anchor' {
      return this.$route.query.mode === 'anchor' ? 'anchor' : 'asset'
    },
    assetsForGroup(): any[] {
      if (!this.groupAssets.length) return []
      const live = this.assetsCache || []
      return this.groupAssets.map((asset: any) => {
        const liveAsset = live.find((la: any) => Number(la.id) === Number(asset.id))
        return liveAsset || asset
      })
    }
  },
  mounted() {
    if (!this.canEdit) {
      this.$router.replace(this.isDraft ? { name: 'AssetSchemes' } : { name: 'AssetSchemePreview', params: { id: this.id } })
      return
    }
    if (this.isDraft) {
      this.initDraft()
      return
    }
    this.fetchScheme()
    this.fetchAssets()
    const refreshMs = parseInt(process.env.VUE_APP_MAP_INTERVAL_REFRESH || '30000')
    this.refreshInterval = setInterval(() => {
      this.fetchAssets()
      this.fetchAnchors()
    }, refreshMs)
  },
  unmounted() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  watch: {
    // Handles the draft -> real scheme transition: createDraftScheme() replaces the route's
    // :id param in place, which updates this prop without remounting the component.
    id() {
      if (this.isDraft) {
        this.initDraft()
      } else {
        this.fetchScheme()
        if (!this.refreshInterval) {
          const refreshMs = parseInt(process.env.VUE_APP_MAP_INTERVAL_REFRESH || '30000')
          this.refreshInterval = setInterval(() => {
            this.fetchAssets()
            this.fetchAnchors()
          }, refreshMs)
        }
      }
    },
    // Only relevant for the top-level "+ Dodaj schemat" flow, where the location isn't
    // preselected -- refreshes the picker once the user chooses one from the dropdown.
    draftGroupId(newGroupId: number | null) {
      if (this.isDraft && !this.presetGroupId) this.fetchDraftPickerData(newGroupId)
    }
  },
  methods: {
    convertPathToUrl(filePath: string): string {
      let relativePath = filePath
      if (filePath.includes('/public/var/uploads/')) {
        relativePath = filePath.substring(filePath.indexOf('/var/uploads/'))
      } else if (filePath.includes('var/uploads/')) {
        const index = filePath.indexOf('var/uploads/')
        relativePath = '/' + filePath.substring(index)
      }

      if (!relativePath.startsWith('/')) {
        relativePath = '/' + relativePath
      }

      const base = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '')
      return base ? `${base}${relativePath}` : relativePath
    },
    // No scheme exists yet -- reset every scheme-bound field to empty and, when the location
    // wasn't preselected (top-level "+ Dodaj schemat"), load the full location list so the
    // panel can offer a dropdown instead.
    async initDraft() {
      this.errorMessage = null
      this.scheme = null
      this.schemeImage = null
      this.schemeMarkers = []
      this.groupAssets = []
      this.anchors = []
      this.calibrationForm = { widthM: null, heightM: null }
      this.draftGroupId = null
      this.draftName = ''
      this.draftFile = null
      this.createError = null
      this.formTouched = false
      if (this.presetGroupId) {
        await this.fetchDraftPickerData(this.presetGroupId)
        return
      }
      this.loading = true
      try {
        const response = await getAssetGroupsWithLocation({ take: 1000 })
        const groups = response.data?.data?.result || []
        this.locationOptions = groups
          .map((g: any) => ({ id: g.id, name: g.name }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name, 'pl'))
      } catch (e: any) {
        this.errorMessage = e?.response?.data?.error || e?.message || this.$t('components.assetSchemes.error')
      } finally {
        this.loading = false
      }
    },
    // The list view already tells the user how many anchors/assets a schemeless location has --
    // the draft picker needs to show the same ones, keyed by AssetGroup id since there's no
    // AssetGroupLocation id (and no scheme) yet.
    async fetchDraftPickerData(groupId: number | null) {
      if (!groupId) {
        this.groupAssets = []
        this.anchors = []
        return
      }
      await Promise.all([
        this.fetchGroupAssets(groupId),
        fetchAnchorsForGroup(groupId).then((anchors: any[]) => { this.anchors = anchors }),
      ])
    },
    triggerDraftFilePicker() {
      (this.$refs.draftFileInputRef as HTMLInputElement | undefined)?.click()
    },
    onDraftFileChange(event: Event) {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0] || null
      this.draftFile = file
      // Nothing's uploaded yet in draft mode -- preview the picked file locally (same
      // FileReader-to-data-URL approach as AssetGroupWithLocationFormModal's image picker)
      // so the canvas actually shows the plan instead of staying blank until after "Zapisz".
      if (!file) {
        this.schemeImage = null
        return
      }
      const reader = new FileReader()
      reader.onload = (e) => {
        this.schemeImage = e.target?.result as string
      }
      reader.readAsDataURL(file)
    },
    // Single bottom "Zapisz" button drives everything -- draft mode creates the scheme (name +
    // file-or-dimensions), existing-scheme mode saves calibration and marker placement together
    // (previously two separate buttons, which was confusing since "Zapisz" already saves markers).
    async save() {
      this.formTouched = true
      if (this.isDraft) {
        if (!this.canCreateDraft) return
        await this.createDraftScheme()
      } else {
        await this.saveExisting()
      }
    },
    async createDraftScheme() {
      if (!this.canCreateDraft || !this.groupId) return
      this.creatingScheme = true
      this.createError = null
      try {
        const fd = new FormData()
        fd.append('scheme_name', this.draftName.trim())
        if (this.draftFile) {
          fd.append('scheme_file', this.draftFile)
        }
        // Independent of the file -- calibrating the image right away is as valid as
        // uploading it bare, same as editing an existing scheme allows both together.
        if (this.calibrationForm.widthM !== null && this.calibrationForm.heightM !== null) {
          fd.append('width_m', String(this.calibrationForm.widthM))
          fd.append('height_m', String(this.calibrationForm.heightM))
        }
        const response = await addAssetGroupScheme(this.groupId, fd)
        const newId = response.data?.scheme?.id
        if (newId) {
          // The picker was already live during the draft, so anchors/assets placed before
          // hitting "Dodaj schemat" would otherwise be silently lost -- persist them now.
          const markers = (this.$refs.editorRef as any)?.getMarkers?.() || []
          if (markers.length) {
            await updateAssetSchemeMarkers(this.groupId, newId, markers)
          }
          this.$router.replace({ name: 'AssetSchemeEdit', params: { id: newId }, query: { mode: 'anchor' } })
        }
      } catch (e: any) {
        this.createError = e?.response?.data?.error || e?.message || this.$t('components.assetSchemes.error')
      } finally {
        this.creatingScheme = false
      }
    },
    async fetchScheme() {
      this.loading = true
      this.errorMessage = null
      try {
        const response = await getAssetScheme(this.id as any)
        const scheme = response.data?.scheme || response.data?.data?.scheme || response.data?.scheme
        this.scheme = scheme
        const path = scheme?.filePath || scheme?.file_path
        this.schemeImage = path ? this.convertPathToUrl(path) : null
        this.schemeMarkers = Array.isArray(scheme?.markersJson) ? scheme.markersJson : []
        this.calibrationForm = { widthM: scheme?.widthM ?? null, heightM: scheme?.heightM ?? null }
        await Promise.all([
          scheme?.group?.id ? this.fetchGroupAssets(scheme.group.id) : Promise.resolve(),
          this.fetchAnchors(),
        ])
      } catch (e: any) {
        this.errorMessage = e?.response?.data?.error || e?.message || this.$t('components.assetSchemes.error')
      } finally {
        this.loading = false
      }
    },
    async fetchAssets() {
      try {
        const response = await getAssetsWithLatestParams()
        this.assetsCache = response.data?.data || []
      } catch (e: any) {
        this.assetsCache = []
      }
    },
    async fetchGroupAssets(groupId: number) {
      try {
        const response = await getAssetGroup(groupId)
        this.groupAssets = response.data?.assets || response.data?.data?.assets || []
      } catch (e: any) {
        this.groupAssets = []
      }
    },
    // Only offer anchors whose gateway is actually assigned to this scheme's location -- mirrors
    // how groupAssets scopes the asset picker to the group's own assets.
    async fetchAnchors() {
      this.anchors = await fetchAnchorsForLocation(this.scheme?.location?.id)
    },
    // Keeps the m/px scale identical on both axes (like Limonfy's AnchorLayout) -- height
    // follows the uploaded image's own aspect ratio instead of being typed independently,
    // which would otherwise skew any future RSSI distance math.
    onWidthInput(event: Event) {
      const widthM = Math.max(0, Number((event.target as HTMLInputElement).value) || 0)
      const aspect = (this.$refs.editorRef as any)?.getImageAspect?.()
      this.calibrationForm.widthM = widthM
      if (aspect) this.calibrationForm.heightM = Math.round((widthM / aspect) * 100) / 100
    },
    // Backend validation messages are English (consistent with every other exception in
    // AssetGroupService) -- translate the ones a user can actually trigger from this panel.
    translateCalibrationError(raw: string | undefined): string | null {
      if (!raw) return null
      const map: Record<string, string> = {
        'widthM must be greater than 0': this.$t('components.assetSchemes.editor.calibration.errors.widthPositive') as string,
        'heightM must be greater than 0': this.$t('components.assetSchemes.editor.calibration.errors.heightPositive') as string,
        'widthM and heightM must be provided together': this.$t('components.assetSchemes.editor.calibration.errors.bothRequired') as string,
        'Cannot clear calibration while anchor markers exist on this scheme; remove anchor markers first': this.$t('components.assetSchemes.editor.calibration.errors.cannotClearWithAnchors') as string
      }
      return map[raw] || raw
    },
    // Merges the saved widthM/heightM back into `scheme` directly instead of re-fetching the
    // whole scheme -- used both by "Wyczyść kalibrację" (its own action) and mid-way through
    // saveExisting(), which still needs schemeMarkers intact for the marker save right after it.
    applyCalibrationResponse(response: any) {
      const updated = response?.data?.scheme || response?.data?.data?.scheme
      if (updated && this.scheme) {
        this.scheme = { ...this.scheme, widthM: updated.widthM, heightM: updated.heightM }
      }
      this.calibrationForm = { widthM: this.scheme?.widthM ?? null, heightM: this.scheme?.heightM ?? null }
    },
    async clearCalibration() {
      if (!this.canEdit || !this.groupId || !this.scheme?.id) return
      this.clearingCalibration = true
      this.calibrationError = null
      try {
        const response = await updateAssetSchemeCalibration(this.groupId, this.scheme.id, { widthM: null, heightM: null })
        this.applyCalibrationResponse(response)
      } catch (e: any) {
        const raw = e?.response?.data?.error || e?.message
        this.calibrationError = this.translateCalibrationError(raw) || this.$t('components.assetSchemes.error')
      } finally {
        this.clearingCalibration = false
      }
    },
    triggerImagePicker() {
      (this.$refs.imageFileInputRef as HTMLInputElement | undefined)?.click()
    },
    async onImageFileChange(event: Event) {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]
      input.value = '' // allow picking the same file again
      if (!file || !this.canEdit || !this.groupId || !this.scheme?.id) return
      this.imageSaving = true
      this.imageError = null
      try {
        const response = await replaceAssetSchemeImage(this.groupId, this.scheme.id, file)
        const updated = response?.data?.scheme || response?.data?.data?.scheme
        if (updated && this.scheme) {
          this.scheme = { ...this.scheme, filePath: updated.filePath }
          this.schemeImage = updated.filePath ? this.convertPathToUrl(updated.filePath) : null
        }
        // Recalibrate height to the new image's own proportions, same as onWidthInput -- best
        // effort, since the <img> needs a moment to actually load before its aspect is known.
        if (this.scheme?.widthM != null) {
          setTimeout(() => {
            const aspect = (this.$refs.editorRef as any)?.getImageAspect?.()
            if (aspect) this.calibrationForm.heightM = Math.round((this.scheme.widthM / aspect) * 100) / 100
          }, 300)
        }
      } catch (e: any) {
        this.imageError = e?.response?.data?.error || e?.message || this.$t('components.assetSchemes.error')
      } finally {
        this.imageSaving = false
      }
    },
    async removeImage() {
      if (!this.canEdit || !this.groupId || !this.scheme?.id) return
      this.imageSaving = true
      this.imageError = null
      try {
        await removeAssetSchemeImage(this.groupId, this.scheme.id)
        this.scheme = { ...this.scheme, filePath: null }
        this.schemeImage = null
      } catch (e: any) {
        this.imageError = e?.response?.data?.error || e?.message || this.$t('components.assetSchemes.error')
      } finally {
        this.imageSaving = false
      }
    },
    async saveExisting() {
      if (!this.canEdit) return
      if (!this.groupId || !this.scheme?.id) return
      if (!this.isCalibrationValid) return
      this.saving = true
      this.errorMessage = null
      this.calibrationError = null
      try {
        const savedWidth = this.scheme.widthM ?? null
        const savedHeight = this.scheme.heightM ?? null
        if (this.calibrationForm.widthM !== savedWidth || this.calibrationForm.heightM !== savedHeight) {
          const calibrationResponse = await updateAssetSchemeCalibration(this.groupId, this.scheme.id, this.calibrationForm)
          this.applyCalibrationResponse(calibrationResponse)
        }
        const markers = (this.$refs.editorRef as any)?.getMarkers?.() || []
        await updateAssetSchemeMarkers(this.groupId, this.scheme.id, markers)
        await this.fetchScheme()
      } catch (e: any) {
        const raw = e?.response?.data?.error || e?.message
        this.errorMessage = this.translateCalibrationError(raw) || this.$t('components.assetSchemes.error')
      } finally {
        this.saving = false
      }
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/asset-scheme-editor.scss';
</style>
<style src="@/assets/scss/asset-view-style.scss" lang="scss"></style>
